import {
  Jacket,
  SCFile,
  StandaloneEndorsement,
  // TitleSearchProduct,
} from "entities/UIModel";
import {
  OrderStatusType,
  PartyRoleType,
  PricingType,
} from "utils/data/enum";
import {
  getCollateralMortgageTransCode,
  isCollateralMortgageTransCode,
} from "../../products/collateralMortgage";
import { hasIssuedLoanJacket } from "../../products/jacket";
import {
  convertToNumber,
  convertToNumberWithNegative,
  dateWithoutTime,
  formatCurrency,
  REGEX_ALPHA_NUMERIC,
  validateEmail,
} from "utils/shared";
import * as yup from "yup";
import { isBefore } from "date-fns";
import { getRoundedValue } from "utils/services/utilsService";
import {
  getEndOfDay,
  VALID_ZIP_LENGTH,
  isForeignAddress,
  effectiveDateValidationMsg,
  isNullOrUndefined,
  locationValidationMsg,
} from "../../helper";
import { SpaceAgencyProductStateConfiguration } from "entities/ApiModel/Space/SpaceAgencyProductStateConfiguration";
import { isValidUwaPolicyInfo } from "../../uwaHelper";

const requiredErrorMessage: string = "Required";
const requiredProposedPolicyMessage: string = "You must add at least one proposed policy before submitting.";
const requiredSpecialRiskMessage: string = "You must add at least one Special Risk or select “No Special Risks” before submitting.";
const requiredTitleExaminedFromMessage: string = "Must be before Title Examined To date";
const requiredTitleExaminedToMessage: string = "Must be after Title Examined From date";


// const uwaPropertyTypeSchema = yup.object().shape({
//   value: yup.string().required(requiredErrorMessage),
// });
// const uwaPolicyInfoSchema = yup.object().shape({
//   uwaPolicyTypeCode: yup
//     .string()
//     .when('uwaPolicyTypeCode', {
//       is: (val: string | undefined) => val !== undefined && val !== '',
//       then: yup.string().min(1, requiredErrorMessage),
//       otherwise: yup.string(),
//     }),
//     policyAmount: yup
//       .string()
//       .when('policyAmount', {
//         is: (val: string | undefined) => val !== undefined && val !== '',
//         then: yup.string().min(1, requiredErrorMessage),
//         otherwise: yup.string(),
//     }),
// })

const validationSchema = yup.object().shape({
  fileNameNumber: yup
    .string()
    .required(requiredErrorMessage)
    .test(
      "MustContainAlphaNumeric",
      "Must have at least one alphanumeric",
      (value, _) => {
        return value?.match(REGEX_ALPHA_NUMERIC) != null;
      }
    )
    .test(
      "isValidFilename",
      "Invalid file name: A valid file name is no more than 100 characters long",
      (value, _) => {
        return !(!value || value.length > 100);
      }
    )
    .test(
      "isDuplicatedFilename",
      " This File Name/Number already exists for this Agency.",
      (value, context) => {
        return !context.options.context?.isDuplicatedFile;
      }
    ),
  agency: yup.object({
    id: yup.string().required(requiredErrorMessage),
    name: yup.string(),
  }),
  agencyLocation: yup
    .object({
      id: yup.string(),
      name: yup.string(),
      isValidationRequired: yup.boolean(),
      isDisabled: yup.boolean(),
    })
    .test(
      "isAgencyLocationRequired",
      requiredErrorMessage,
      (value, context) => {
        return validateAgencyLocation(context) ? Boolean(value.name) : true;
      }
    )
    .test(
      "isAgencyLocationValid",
      "A supported Agency Location is needed. Please contact the Agency Support Center for assistance.",
      (value, context) => {
        return validateAgencyLocation(context)
          ? Boolean(value.name) && !value.isDisabled
          : true;
      }
    ),
  transactionTypeCode: yup.string().when("isTransactionTypeRequired", {
    is: true,
    then: yup.string().required(requiredErrorMessage),
  }),
  properties: yup.array().of(
    yup.object({
      addressOne: yup
        .string()
        .test({
          name: "Required for Title Search submit",
          test: function (value, context) {
            const root = getValuesFromContext(context);

            if (!(root.properties[0].isTitleSearchCountyValidationRequired || false)) {
              return true;
            }

            if (value && value.trim().length > 0) {
              return true
            }
            return context.createError({ message: requiredErrorMessage });
          }
        }),
      addressTwo: yup.string(),
      city: yup
        .string()
        .test({
          name: "Required for Title Search submit",
          test: function (value, context) {
            const root = getValuesFromContext(context);

            if (!(root.properties[0].isTitleSearchCountyValidationRequired || false)) {
              return true;
            }

            if (value && value.trim().length > 0) {
              return true;
            }
            return context.createError({ message: requiredErrorMessage });
          }
        }),
      isValidationRequired: yup.boolean(),
      county: yup
        .object({
          code: yup.string(),
          name: yup.string(),
        })
        .test(
          "PropertyCountyRequired",
          requiredErrorMessage,
          (value, context) => {
            return validateProperties(context) ? Boolean(value.code) : true;
          }
        )
        .test({
          name: "Must be a valid county ",
          test: function (value, context) {
            const root = getValuesFromContext(context);
            const contextTsAgencyConfig = getContextTsAgencyConfig(context);

            if (!(root.properties[0].isTitleSearchCountyValidationRequired || false)) {
              return true;
            }
            const matchedCounty = contextTsAgencyConfig?.state?.counties
              ?.find((c) => c.countyFips === value.code ||
                c.countyName?.toLowerCase() === value.name?.toLowerCase()
              );

            if (matchedCounty) {
              return true;
            }
            else {
              return context.createError({ message: "Invalid county" });
            }
          }
        }),
      legalDescription: yup.string(),
      propertyType: yup.string(),
      state: yup
        .object({
          abbr: yup.string(),
          code: yup.string(),
        })
        .test(
          "PropertyStateRequired",
          requiredErrorMessage,
          (value, context) => {
            return validateProperties(context)
              ? Boolean(value.abbr && value.abbr !== "Select")
              : true;
          }
        ),
      zipCode: yup
        .string()
        .test(
          "isValidZip",
          "The zip code must be 5 or 9 numbers",
          (value, context) =>
            hasZipCodeValidation(context) ? isValidZipCode(value) : true
        )
        .test({
          name: "Required for Title Search submit",
          test: function (value, context) {
            const root = getValuesFromContext(context);

            if (!(root.properties[0].isTitleSearchCountyValidationRequired || false)) {
              return true;
            }

            if (value && value.trim().length > 0) {
              return true;
            }
            return context.createError({ message: requiredErrorMessage });
          }
        }),
    })
  ),
  buyerBorrowerParties: yup.array().of(
    yup.object().shape({
      partyTypeCode: yup.string().required(),
      addressOne: yup.string().nullable(),
      addressTwo: yup.string().nullable(),
      city: yup.string().nullable(),
      state: yup
        .object({
          abbr: yup.string(),
          code: yup.string(),
        })
        .nullable(),
      zipCode: yup
        .string()
        .test(
          "isValidZip",
          "The zip code must be 5 or 9 numbers",
          (value, context) =>
            hasZipCodeValidation(context) ? isValidZipCode(value) : true
        ),
      suffixTypeCode: yup.string(),
      isExcludedOnCPL: yup.boolean(),
      partyFirstName: yup.string().when("requirePartyValidation", {
        is: true,
        then: yup
          .string()
          .when("partyTypeCode", (value: string, field: yup.StringSchema) =>
            getIndividualFieldRequired(value, field)
          ),
      }),
      partyMiddleName: yup.string(),
      partyLastName: yup.string().when("requirePartyValidation", {
        is: true,
        then: yup
          .string()
          .when("partyTypeCode", (value: string, field: yup.StringSchema) =>
            getIndividualFieldRequired(value, field)
          ),
      }),
      companyName: yup.string().when("requirePartyValidation", {
        is: true,
        then: yup
          .string()
          .when("partyTypeCode", (value: string, field: yup.StringSchema) =>
            getCompanyFieldRequired(value, field)
          ),
      }),
    })
  ),
  lenderParties: yup.array().of(
    yup.object().shape({
      partyTypeCode: yup.string().required(),
      addressOne: yup.string().nullable(),
      addressTwo: yup.string().nullable(),
      city: yup.string().nullable(),
      state: yup
        .object({
          abbr: yup.string(),
          code: yup.string(),
        })
        .nullable(),
      zipCode: yup
        .string()
        .test(
          "isValidZip",
          "The zip code must be 5 or 9 numbers",
          (value, context) =>
            hasZipCodeValidation(context) ? isValidZipCode(value) : true
        )
        .nullable(),
      suffixTypeCode: yup.string(),
      isExcludedOnCPL: yup.boolean(),
      companyName: yup
        .string()
        .when(
          "requirePartyValidation",
          (value: boolean, field: yup.StringSchema) =>
            getAddressFieldRequired(value, field)
        ),
    })
  ),
  sellerParties: yup.array().of(
    yup.object().shape({
      partyTypeCode: yup.string().required(),
      addressOne: yup.string().nullable(),
      addressTwo: yup.string().nullable(),
      city: yup.string().nullable(),
      state: yup
        .object({
          abbr: yup.string(),
          code: yup.string(),
        })
        .nullable(),
      zipCode: yup
        .string()
        .test(
          "isValidZip",
          "The zip code must be 5 or 9 numbers",
          (value, context) =>
            hasZipCodeValidation(context) ? isValidZipCode(value) : true
        ),
      suffixTypeCode: yup.string(),
      isExcludedOnCPL: yup.boolean(),
      partyFirstName: yup.string(),
      partyMiddleName: yup.string(),
      partyLastName: yup.string(),
      companyName: yup.string(),
    })
  ),
  cpls: yup.array().of(
    yup.object({
      isValidationRequired: yup.boolean(),
      addresseeNames: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (
                context.parent &&
                context.parent.addresseeNames &&
                context.parent.addresseeNames.trim().length > 0
              ) {
                return true;
              }

              return false;
            }),
        }),
      coveredParty: yup.string().nullable(),
      effectiveDate: yup
        .date()
        .nullable()
        //.required(requiredErrorMessage)
        .when("isValidationRequired", {
          is: true,
          then: yup
            .date()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              return !!value;
            })
            .test({
              name: "Validating effective date",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = effectiveDateValidationMsg(
                  index,
                  contextValues?.cpls
                );
                return msg ? context.createError({ message: msg }) : true;
              },
            }),
        }),
      form: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (value && value.length > 0) return true;
              return false;
            }),
        }),
      pricingDetail: yup.object({
        locationDisplayName: yup
          .string()
          .nullable()
          .when("isValidatingLocationRequired", {
            is: true,
            then: yup
              .string()
              .nullable()
              .test(
                "locationRequired",
                requiredErrorMessage,
                (value, context) => {
                  if (value && value.length > 0) return true;
                  else return false;
                }
              )
              // .test(
              //   "validateLocation",
              //   "Invalid Agency Location",
              //   (value, context) => {
              //     return validateProductAgencyLocation(value || "", context);
              //   }
              // ),
              .test({
                name: "Validating product's agency location",
                test: function (value, context) {
                  const contextValues = getValuesFromContext(context);
                  const index = getIndexFromPath(this.path);
                  const msg = locationValidationMsg(
                    index,
                    contextValues?.cpls
                  );
                  return msg ? context.createError({ message: msg }) : true;
                },
              }),              
          }),
      }),
      includeAllBranches: yup.bool(),
      addressOne: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              return validateStringField(value || "");
            }),
        }),
      addressTwo: yup.string().nullable(),
      city: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, function (value, context) {
              const contextValues = getValuesFromContext(context);
              const index = parseInt(this.path.split("[")[1].split("]")[0], 10);
              if (isForeignAddress(index, contextValues.cpls)) return true;
              return validateStringField(value || "");
            }),
        }),
      state: yup
        .object({
          code: yup.string(),
          abbr: yup
            .string()
            .test(
              "State Required",
              requiredErrorMessage,
              function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = parseInt(
                  this.path.split("[")[1].split("]")[0],
                  10
                );
                if (isForeignAddress(index, contextValues.cpls)) return true;

                const values: any[] = (context as any).from;
                if (values && values.length >= 2) {
                  const root = values[values.length - 2].value;
                  if (root.isValidationRequired && !context.parent.code)
                    return false;
                }
                return true;
              }
            ),
        })
        .nullable(),
      zipCode: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, function (value, context) {
              const contextValues = getValuesFromContext(context);
              const index = parseInt(this.path.split("[")[1].split("]")[0], 10);
              if (isForeignAddress(index, contextValues.cpls)) return true;
              return validateStringField(value || "");
            }),
        }),
      foreignCountry: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, function (value, context) {
              const contextValues = getValuesFromContext(context);
              const index = parseInt(this.path.split("[")[1].split("]")[0], 10);
              if (!isForeignAddress(index, contextValues.cpls)) return true;
              return validateStringField(value || "");
            }),
        }),
      buyerBorrower: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (
                context.parent &&
                context.parent.buyerBorrower &&
                context.parent.buyerBorrower.trim().length > 0
              ) {
                return true;
              }

              return false;
            }),
        }),

      seller: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (
                context.parent &&
                context.parent.coveredParty &&
                context.parent.coveredParty.toUpperCase() !==
                PartyRoleType.Seller
              )
                return true;
              if (
                context.parent &&
                context.parent.seller &&
                context.parent.seller.trim().length > 0
              ) {
                return true;
              }

              return false;
            }),
        }),
    })
  ),
  aALProducts: yup.array().of(
    yup.object({
      isValidationRequired: yup.boolean(),
      addresseeNames: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (
                context.parent &&
                context.parent.addresseeNames &&
                context.parent.addresseeNames.trim().length > 0
              ) {
                return true;
              }

              return false;
            }),
        }),
      coveredParty: yup.string().nullable(),
      effectiveDate: yup
        .date()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .date()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              return !!value;
            })
            .test({
              name: "Validating effective date",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = effectiveDateValidationMsg(
                  index,
                  contextValues?.aALProducts
                );
                return msg ? context.createError({ message: msg }) : true;
              },
            }),
        }),
      form: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (value && value.length > 0) return true;
              return false;
            }),
        }),
      pricingDetail: yup.object({
        locationDisplayName: yup
          .string()
          .nullable()
          .when("isValidatingLocationRequired", {
            is: true,
            then: yup
              .string()
              .nullable()
              .test(
                "locationRequired",
                requiredErrorMessage,
                (value, context) => {
                  if (value && value.length > 0) return true;
                  else return false;
                }
              )
              // .test(
              //   "validateLocation",
              //   "Invalid Agency Location",
              //   (value, context) => {
              //     return validateProductAgencyLocation(value || "", context);
              //   }
              // ),
              .test({
                name: "Validating product's agency location",
                test: function (value, context) {
                  const contextValues = getValuesFromContext(context);
                  const index = getIndexFromPath(this.path);
                  const msg = locationValidationMsg(
                    index,
                    contextValues?.aALProducts
                  );
                  return msg ? context.createError({ message: msg }) : true;
                },
              }),                 
          }),
      }),
      includeAllBranches: yup.bool(),
      addressOne: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              return validateStringField(value || "");
            }),
        }),
      addressTwo: yup.string().nullable(),
      city: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, function (value, context) {
              const contextValues = getValuesFromContext(context);
              const index = parseInt(this.path.split("[")[1].split("]")[0], 10);
              if (isForeignAddress(index, contextValues.aALProducts))
                return true;
              return validateStringField(value || "");
            }),
        }),
      state: yup
        .object({
          code: yup.string(),
          abbr: yup
            .string()
            .test(
              "State Required",
              requiredErrorMessage,
              function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = parseInt(
                  this.path.split("[")[1].split("]")[0],
                  10
                );
                if (isForeignAddress(index, contextValues.aALProducts))
                  return true;

                const values: any[] = (context as any).from;
                if (values && values.length >= 2) {
                  const root = values[values.length - 2].value;
                  if (root.isValidationRequired && !context.parent.code)
                    return false;
                }
                return true;
              }
            ),
        })
        .nullable(),
      zipCode: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, function (value, context) {
              const contextValues = getValuesFromContext(context);
              const index = parseInt(this.path.split("[")[1].split("]")[0], 10);
              if (isForeignAddress(index, contextValues.aALProducts))
                return true;
              return validateStringField(value || "");
            }),
        }),
      foreignCountry: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, function (value, context) {
              const contextValues = getValuesFromContext(context);
              const index = parseInt(this.path.split("[")[1].split("]")[0], 10);
              if (!isForeignAddress(index, contextValues.aALProducts))
                return true;
              return validateStringField(value || "");
            }),
        }),
      buyerBorrower: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (
                context.parent &&
                context.parent.buyerBorrower &&
                context.parent.buyerBorrower.trim().length > 0
              ) {
                return true;
              }

              return false;
            }),
        }),
    })
  ),
  jackets: yup.array().of(
    yup.object().shape({
      effectiveDate: yup
        .date()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .date()
            .nullable()
            .required(requiredErrorMessage)
            .test({
              name: "Validating effective date",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = effectiveDateValidationMsg(
                  index,
                  contextValues?.jackets
                );
                return msg ? context.createError({ message: msg }) : true;
              },
            }),
        }),
      form: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test({
              name: "Validating form based on effective date",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                if (!contextValues?.jackets) return true;
                const msg = contextValues?.jackets[index].formValidationMsg;
                return msg ? context.createError({ message: msg }) : true;
              },
            }),
        }),
      primaryLoanOrderID: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              const jacket: any = context.parent;

              if (
                !isCollateralMortgageTransCode(
                  jacket?.pricingDetail?.transCode,
                  getCollateralMortgageTransCodeFromContext(context)
                )
              )
                return true;

              // Mirrors isOrphanedCollateralMortgage in JacketProduct: an issued
              // Collateral Mortgage with no primary loan left to attach to hides the
              // control, so it cannot be required.
              if (
                jacket?.orderStatusTypeCode === OrderStatusType.Issued &&
                !hasIssuedLoanJacket(
                  getValuesFromContext(context)?.jackets,
                  getIndexFromPath(context.path)
                )
              )
                return true;

              return Boolean(value);
            }),
        }),
      pricingDetail: yup.object({
        transCodeKey: yup
          .string()
          .nullable()
          .when("isValidationRequired", {
            is: true,
            then: yup
              .string()
              .nullable()
              .test("", requiredErrorMessage, (value, context) => {
                const contextFrom: any[] = (context as any).from;
                const jacketRoot = contextFrom[contextFrom.length - 2].value;

                if (jacketRoot && jacketRoot.isTransCodeRequired) {
                  return Boolean(value);
                }

                return true;
              }),
          }),
        liability: yup.string().when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .test("", requiredErrorMessage, (value) => {
              return getRoundedValue(value) > 0;
            })
            .test("validateLiability", "", function (value, context) {
              const contextValues = getValuesFromContext(context);
              const liabilityConfig = getLiabilityConfig(context);

              if (liabilityConfig?.length === 0) return true;

              const index = parseInt(this.path.split("[")[1].split("]")[0], 10);
              const liabilityFormConfig = liabilityConfig.find((c) => c.form === contextValues.jackets[index].form);

              if (liabilityFormConfig && liabilityFormConfig.configValue) {
                if (
                  getRoundedValue(value) > parseInt(liabilityFormConfig.configValue)
                ) {
                  return this.createError({
                    message: `Liability amount cannot exceed ${formatCurrency(
                      parseInt(liabilityFormConfig.configValue)
                    )}`,
                  });
                }
              }

              // const booleanFormValue =
              //   contextValues.jackets[index].form === liabilityConfig.form;
              // if (
              //   booleanFormValue &&
              //   getRoundedValue(value) > parseInt(liabilityConfig.configValue)
              // ) {
              //   return this.createError({
              //     message: `Liability amount cannot exceed ${formatCurrency(
              //       parseInt(liabilityConfig.configValue)
              //     )}`,
              //   });
              // }

              return true;
            }),
        }),
        locationDisplayName: yup
          .string()
          .nullable()
          .when("isValidatingLocationRequired", {
            is: true,
            then: yup
              .string()
              .nullable()
              .test(
                "locationRequired",
                requiredErrorMessage,
                (value, context) => {
                  if (value && value.length > 0) return true;
                  else return false;
                }
              )
              // .test(
              //   "validateLocation",
              //   "Invalid Agency Location",
              //   (value, context) => {
              //     return validateProductAgencyLocation(value || "", context);
              //   }
              // ),
              .test({
                name: "Validating product's agency location",
                test: function (value, context) {
                  const contextValues = getValuesFromContext(context);
                  const index = getIndexFromPath(this.path);
                  const msg = locationValidationMsg(
                    index,
                    contextValues?.jackets
                  );
                  return msg ? context.createError({ message: msg }) : true;
                },
              }),                 
          }),
      }),
      /* TODO: Remove this section after business confirms this should for sure be completely removed */
      /*
      originalJacketDate: yup
        .date()
        .nullable()
        .test("", requiredErrorMessage, (value, context) => {
          if (!shouldValidateJacketOPN(context)) return true;
          const isRequired =
            context.parent.originalJacketLiability ||
            context.parent.originalJacketNumber ||
            context.parent.originalJacketType;

          return isRequired ? !!value : true;
        }),
      originalJacketLiability: yup
        .number()
        .nullable()
        .test("", requiredErrorMessage, (value, context) => {
          if (!shouldValidateJacketOPN(context)) return true;
          const isRequired =
            context.parent.originalJacketDate ||
            context.parent.originalJacketNumber ||
            context.parent.originalJacketType;

          return isRequired ? !!value : true;
        }),
      originalJacketNumber: yup
        .string()
        .nullable()
        .test("", requiredErrorMessage, (value, context) => {
          if (!shouldValidateJacketOPN(context)) return true;

          const isRequired =
            context.parent.originalJacketLiability ||
            context.parent.originalJacketDate ||
            context.parent.originalJacketType;

          return isRequired ? !!value : true;
        }),
      originalJacketType: yup
        .string()
        .nullable()
        .test("", requiredErrorMessage, (value, context) => {
          if (!shouldValidateJacketOPN(context)) return true;
          const isRequired =
            context.parent.originalJacketLiability ||
            context.parent.originalJacketNumber ||
            context.parent.originalJacketDate;

          return isRequired ? !!value : true;
        }),*/
      endorsements: yup.array().of(
        yup.object({
          effectiveDate: yup
            .date()
            .nullable()
            .when("isValidationRequired", {
              is: true,
              then: yup
                .date()
                .nullable()
                .test(
                  "if the endorsement is required",
                  requiredErrorMessage,
                  (value, context) => isValidEndorsement(context) || Boolean(value)
                )
                .test(
                  "equal to or greater than jacket effective date",
                  // "The effective date must be equal to or<br />greater than the Jacket Effective Date",
                  "Must be the same or greater than Jacket Effective Date",
                  (value, context) => {
                    // Notes: 
                    //  Skip validation if it's not required.
                    //  The isValidEndoresment will return true if the endorsement does not need to be validated
                    if (isValidEndorsement(context)) return true;

                    const jacket: Jacket = (context as any).from[1].value;
                    jacket.effectiveDate?.setHours(0, 0, 0, 0);

                    return !isBefore(
                      new Date(value as Date),
                      jacket.effectiveDate!!
                    );
                  }
                )
                .test({
                  name: "Validating endorsement effective date",
                  test: function (value, context) {
                    // console.log("context:", context);

                    //  Skip validation if it's not required.
                    //  The isValidEndoresment will return true if the endorsement does not need to be validated
                    if (isValidEndorsement(context)) return true;

                    const msg = context?.parent?.effectiveDateValidationMsg || "";
                    // const contextValues = getValuesFromContext(context);
                    // const index = getIndexFromPath(this.path);
                    // console.log("contextValues:", contextValues, "- index:", index);
                    // const msg = effectiveDateValidationMsg(
                    //   index,
                    //   contextValues?.jackets
                    // );
                    return msg ? context.createError({ message: msg }) : true;
                    // return true; // temp
                  },
                }),
            }), // eof effectivedate
          pricingDetail: yup.object({
            transCodeKey: yup
              .string()
              .nullable()
              .test("", requiredErrorMessage, (value, context) => {
                const contextFrom: any[] = (context as any).from;
                const jacketRoot = contextFrom[contextFrom.length - 2].value;
                const endo = contextFrom[contextFrom.length - 3].value;

                if (jacketRoot && jacketRoot.isTransCodeRequired) {
                  if (endo.isSFE) {
                    if (endo.isSelected && !value) {
                      return false;
                    } else {
                      return true;
                    }
                  }
                  return Boolean(value);
                }

                return true;
              }),
          }), //eof pricing detail
        }) // eof yup object
      ),
    })
  ),
  standaloneEndorsements: yup.array().of(
    yup.object().shape({
      // originalJacketDate: yup
      //   // .date()
      //   .string()
      //   .nullable()
      //   .when("isValidationRequired", {
      //     is: true,
      //     then: yup
      //       // .date()
      //       .string()
      //       .nullable()
      //       .required(requiredErrorMessage)
      //       .test("Must be a valid date", "Must be a valid date", (value) => {
      //         return isValidDate(new Date(value as string));
      //       })
      //     .test("Must be greter than today", "Original Policy Date cannot be future date", (value) => {
      //       const currentDate = (new Date()).setHours(0,0,0,0);
      //       console.log('currentDate:', currentDate);
      //       console.log('input date:', value);
      //       return isBefore((new Date(value as string)).setHours(0,0,0,0), currentDate);
      //     }),
      //   }), // eof originalJacketDate
      pricingDetail: yup.object({
        locationDisplayName: yup
          .string()
          .nullable()
          .when("isValidatingLocationRequired", {
            is: true,
            then: yup
              .string()
              .nullable()
              .test(
                "locationRequired",
                requiredErrorMessage,
                (value, context) => {
                  if (value && value.length > 0) return true;
                  else return false;
                }
              )
              // .test(
              //   "validateLocation",
              //   "Invalid Agency Location",
              //   (value, context) => {
              //     return validateProductAgencyLocation(value || "", context);
              //   }
              // ),
              .test({
                name: "Validating product's agency location",
                test: function (value, context) {
                  const contextValues = getValuesFromContext(context);
                  const index = getIndexFromPath(this.path);
                  const msg = locationValidationMsg(
                    index,
                    contextValues?.standaloneEndorsements
                  );
                  return msg ? context.createError({ message: msg }) : true;
                },
              }),                 
          }),
      }),
      endorsements: yup.array().of(
        yup.object({
          effectiveDate: yup
            .string()
            .nullable()
            .when("isValidationRequired", {
              is: true,
              then: yup
                .string()
                .nullable()
                .test("Required", requiredErrorMessage, (value, context) => {
                  return (
                    isIssuable((context as any).from[1].value) &&
                    !isNullOrUndefined(value)
                  );
                })
                .test(
                  "Must be a valid date",
                  "Must be a valid date",
                  (value) => {
                    return isValidDate(new Date(value as string));
                  }
                )
                .test(
                  "equal to or greater than jacket effective date",
                  // "The effective date must be equal to or<br />greater than the Original Policy Date",
                  "Must be the same or greater than Original Policy Date",
                  (value, context) => {
                    const standaloneEndorsement: StandaloneEndorsement = (
                      context as any
                    ).from[1].value;

                    standaloneEndorsement.originalJacketDate?.setHours(0, 0, 0, 0);

                    return (
                      !isIssuable(standaloneEndorsement) ||
                      !isBefore(
                        new Date(value as string),
                        standaloneEndorsement.originalJacketDate!!
                      )
                    );
                  }
                )
                .test({
                  name: "Validating endorsement effective date",
                  test: function (value, context) {
                    //  Skip validation if it's not required.
                    //  The isValidEndoresment will return true if the endorsement does not need to be validated
                    if (isValidEndorsement(context)) return true;

                    const msg = context?.parent?.effectiveDateValidationMsg || "";
                    return msg ? context.createError({ message: msg }) : true;
                    // return true; // temp
                  }
                })
                .test({
                  //  Standalone only: the product's single agency location must still be valid for
                  //  this endorsement's own effective date. Read parent-relative, as the flat
                  //  locationValidationMsg(index, items) helper cannot reach a nested endorsement.
                  name: "Validating endorsement agency location effective date",
                  test: function (value, context) {
                    if (isValidEndorsement(context)) return true;

                    const msg = context?.parent?.locationValidationMsg || "";
                    return msg ? context.createError({ message: msg }) : true;
                  }
                }
              ),
            }), // eof effectivedate
          pricingDetail: yup.object({
            transCodeKey: yup
              .string()
              .nullable()
              .test("", requiredErrorMessage, (value, context) => {
                const contextFrom: any[] = (context as any).from;
                // console.log('contextFrom: ', contextFrom);
                const productRoot = contextFrom[contextFrom.length - 2].value;

                if (productRoot && productRoot.isTransCodeRequired) {
                  return Boolean(value);
                }
                return true;
              }),
          }), //eof pricing detail
        })
      ),
    })
  ),
  titleSearchProducts: yup.array().of(
    yup.object({
      isValidationRequired: yup.boolean(),
      transactionType: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (value && value.length > 0) return true;
              return false;
            }),
        }),
      stewartProduct: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test("", requiredErrorMessage, (value, context) => {
              if (value && value.length > 0) return true;
              return false;
            }),
        }),
      needByDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => orig === "" ? null : curr)
        .when("isValidationRequired", {
          is: true,
          then: yup
            .date()
            .nullable()
            .test({
              name: "Must be today or future date",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = validateTitleSearchNeedByDate(
                  index,
                  contextValues?.titleSearchProducts
                );
                return msg ? context.createError({ message: msg }) : true;
              }

            }),
        }),
      notificationEmailAddresses: yup
        .string()
        .nullable()
        .when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test({
              name: "Must be a valid email address format",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = validateEmails(
                  contextValues?.titleSearchProducts[index]?.notificationEmailAddresses
                );
                return msg ? context.createError({ message: msg }) : true;
              }
            }),
        }),
      notificationEmailAddressesEdited: yup
        .string()
        .nullable()
        .when("isEmailEditedValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test({
              name: "Must be a valid email address format",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = validateEmails(
                  contextValues?.titleSearchProducts[index]?.notificationEmailAddressesEdited
                );

                return msg ? context.createError({ message: msg }) : true;
              }

            }),
        }),
      messageNeedByDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => orig === "" ? null : curr)
        .when("isUpdateOrderValidationRequired", {
          is: true,
          then: yup
            .date()
            .nullable()
            .required(requiredErrorMessage)
            .test({
              name: "Must be today or future date",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = validateTitleSearchMessageNeedByDate(
                  index,
                  contextValues?.titleSearchProducts
                );
                return msg ? context.createError({ message: msg }) : true;
              }
            }),
        }),
    }),
  ),
  uwaProducts: yup.array().of(
    yup.object({
      isValidationRequired: yup.boolean(),
      notificationEmailAddresses: yup
        .string()
        .nullable()
        .when("isEmailFormatValidationRequired", {
          is: true,
          then: yup
            .string()
            .nullable()
            .test({
              name: "Must be a valid email address format",
              test: function (value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const msg = validateEmails(
                  contextValues?.uwaProducts[index]?.notificationEmailAddresses
                );
                // console.log("contextValues: ", contextValues);
                // console.log('msg: ', msg);
                return msg ? context.createError({ message: msg }) : true;
              }
            })
          .when("isValidationRequired", {
            is: true,
            then: (schema) => schema.required(requiredErrorMessage),
          })
        }),
      uwaRequest: yup.object({
        propertyTypes: yup
          .array()
          .of(
            yup.object({
              text: yup.string().nullable(),
              value: yup.string().nullable(),
            })
          )
          .test({
            name: "Must at least have one property type",
            test: function(propertyTypes, context) {
              const contextValues = getValuesFromContext(context);
              const index = getIndexFromPath(this.path);
              const isRequired  = contextValues?.uwaProducts[index]?.isValidationRequired || false;
              // console.log("contextValues: ", contextValues);
              // console.log("index:", index);
              // console.log("isRequired:", isRequired);
              // console.log("propertyTypes:", propertyTypes);
              // console.log("Array.isArray(value):", Array.isArray(propertyTypes));
              
              let isValid = true;
              if (isRequired) {
                if (!Array.isArray(propertyTypes)) {
                  isValid = false;
                }
              }

              return !isValid ? context.createError({message: requiredErrorMessage}) : true;
            }
          }),
          summary: yup
          .string()
          .nullable()
          .test({
            name: "summary-max-500",
            message: "Cannot exceed 500 characters",
            test: function (value, context) {
              const contextValues = getValuesFromContext(context);
              const index = getIndexFromPath(this.path);
              const isRequired =
                contextValues?.uwaProducts?.[index]?.isValidationRequired || false;
        
              if (!value || !isRequired) return true;
        
              return value.length <= 500;
            },
          }),          
        titleExaminedFrom: yup
          .date()
          .nullable()
          .test({
            name: "Must be after Title Examined From date",
            test: function (titleExaminedFrom, context) {
              const contextValues = getValuesFromContext(context);
              const index = getIndexFromPath(this.path);
              const isRequired = contextValues?.uwaProducts[index]?.isValidationRequired || false;
              const fromDate = contextValues?.uwaProducts[index]?.uwaRequest?.titleExaminedFrom;
              const toDate = contextValues?.uwaProducts[index]?.uwaRequest?.titleExaminedTo;
              // console.log("contextValues: ", contextValues);
              // console.log("index:", index);
              // console.log("isRequired:", isRequired);
              // console.log("fromDate:", fromDate);
              // console.log("toDate:", toDate);

              let isValid = true;
              if (isRequired) {
                if (fromDate && toDate) {
                     const fromDateOnly = dateWithoutTime(fromDate);
                     const toDateOnly = dateWithoutTime(toDate);
                     if (!(toDateOnly > fromDateOnly)) {
                      isValid = false;
                     }
                }
              }

              return !isValid ? context.createError({ message: requiredTitleExaminedFromMessage }) : true;
            }
          }),     
        titleExaminedTo: yup
          .date()
          .nullable()
          .test({
            name: "Must be after Title Examined From date",
            test: function (titleExaminedTo, context) {
              const contextValues = getValuesFromContext(context);
              const index = getIndexFromPath(this.path);
              const isRequired = contextValues?.uwaProducts[index]?.isValidationRequired || false;
              const fromDate = contextValues?.uwaProducts[index]?.uwaRequest?.titleExaminedFrom;
              const toDate = contextValues?.uwaProducts[index]?.uwaRequest?.titleExaminedTo;
              // console.log("contextValues: ", contextValues);
              // console.log("index:", index);
              // console.log("isRequired:", isRequired);
              // console.log("fromDate:", fromDate);
              // console.log("toDate:", toDate);

              let isValid = true;
              if (isRequired) {
                if (fromDate && toDate) {
                     const fromDateOnly = dateWithoutTime(fromDate);
                     const toDateOnly = dateWithoutTime(toDate);
                     if (!(toDateOnly > fromDateOnly)) {
                      isValid = false;
                     }
                }
              }

              return !isValid ? context.createError({ message: requiredTitleExaminedToMessage }) : true;
            }
          }),          

      }),
      messages: yup
      .array()
      .of(
        yup.object({
          messageText: yup
            .string()
            .nullable()
            .test({
              name: "messageText-max-2500",
              message: "Cannot exceed 2500 characters",
              test(value, context) {
                const contextValues = getValuesFromContext(context);
                const index = getIndexFromPath(this.path);
                const isRequired =
                  contextValues?.uwaProducts?.[index]?.isValidationRequired || false;
    
                if (!value || !isRequired) return true;
                return value.length <= 2500;
              },
            }),
        })
      ),    
      uwaPolicyInfos: yup
        .array()
        .of(
          yup.object({
            uwaPolicyTypeCode: yup
              .string()
              .nullable()
              .when("isValidationRequired", {
                is: true,
                then: yup.string().nullable().required(requiredErrorMessage),
              }),
            policyAmount: yup
              .string()
              .nullable()
              .when("isValidationRequired", {
                is: true,
                then: yup.string().nullable().required(requiredErrorMessage),
              })
          })
        )
        .test({
          name: "You must add at least one proposed policy before submitting",
          test: function (uwaPolicyInfos, context) {
            const contextValues = getValuesFromContext(context);
            const index = getIndexFromPath(this.path);
            const isRequired = contextValues?.uwaProducts[index]?.isValidationRequired || false;
            const contextValueUwaPolicyInfos = contextValues?.uwaProducts[index]?.uwaPolicyInfos;
            // console.log("contextValues: ", contextValues);
            // console.log("index:", index);
            // console.log("isRequired:", isRequired);
            // console.log("uwaPolicyInfos:", uwaPolicyInfos);
            // console.log("contextValueUwaPolicyInfos:", contextValueUwaPolicyInfos);

            let isValid = true;
            if (isRequired) {
              const itemCount = contextValueUwaPolicyInfos?.filter((pi) => isValidUwaPolicyInfo(pi))?.length || 0;

              if (itemCount === 0) {
                isValid = false;
              } 
            }

            return !isValid ? context.createError({ message: requiredProposedPolicyMessage }) : true;
          }
        }),
      uwaRequestInfo: yup.object({
        specialRisks: yup
          .array()
          .of(yup.object({
            text: yup.string().nullable(),
            value: yup.string().nullable(),
          }))
          .test({
            name: "You must add at least one proposed policy before submitting",
            test: function (specialRisks, context) {
              const contextValues = getValuesFromContext(context);
              const index = getIndexFromPath(this.path);
              const isRequired = contextValues?.uwaProducts[index]?.isValidationRequired || false;
              const isNoSpecialRisks = contextValues?.uwaProducts[index]?.uwaRequestInfo?.isNoSpecialRisks || false;

              let isValid = true;
              if (isRequired && !isNoSpecialRisks) {
                const itemCount = specialRisks?.filter((spr) => !!spr.value)?.length || 0 ;
                if (itemCount === 0) {
                  isValid = false;
                }
              }

              return !isValid ? context.createError({ message: requiredSpecialRiskMessage }) : true;
            }
          }),
      }),
    }),
  ),
  pricing: yup.object({
    isValidationRequired: yup.boolean(),
    coInsurancePct: yup
      .string()
      .nullable()
      .when("isValidationRequired", {
        is: true,
        then: yup
          .string()
          .test(
            "coInsurancePct",
            "Co-Insurance must be between 0 and 100",
            (value, context) => {
              const root = getValuesFromContext(context);
              if (!root.pricing.isCoInsurance) return true;
              const valueNumber = convertToNumberWithNegative(value);
              return valueNumber >= 0 && valueNumber <= 100;
            }
          ),
      }),
  }),
  pricingProducts: yup.array().of(
    yup.object({
      isValidationRequired: yup.boolean(),
      actualFee: yup.string().when("isValidationRequired", {
        is: true,
        then: yup.string().when("pricingType", {
          is: (pricingType: string) => pricingType === PricingType.Tax,
          then: yup
            .string()
            .test(
              "taxAmount",
              "Tax Amount cannot exceed Product Premiums",
              (value, context) => {
                const tax = convertToNumber(value);
                const totalProductPremium = convertToNumber(
                  context.parent.totalProductPremium
                );
                return tax <= totalProductPremium;
              }
            ),
        }),
      }),

      actualRiskRate: yup.string().when("isValidationRequired", {
        is: true,
        then: yup
          .string()
          .test(
            "actualRiskRate",
            "Risk Rate cannot exceed Premium/Fee",
            (value, context) => {
              if (!showRiskRate(context)) return true;
              const valueNumber = convertToNumber(value);
              const fee = convertToNumber(context.parent.actualFee);
              return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
            }
          )
          .test(
            "actualRiskRate",
            "Risk Rate cannot be negative",
            (value, context) => {
              if (!showRiskRate(context)) return true;
              const valueNumber = convertToNumber(value);
              return valueNumber >= 0;
            }
          ),
      }),

      agentRetention: yup.string().when("isValidationRequired", {
        is: true,
        then: yup
          .string()
          .test(
            "agentRetention",
            "Agent Retention cannot exceed Premium/Fee",
            (value, context) => {
              if (showRiskRate(context)) return true;
              const valueNumber = convertToNumber(value);
              const fee = convertToNumber(context.parent.actualFee);
              return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
            }
          )
          .test(
            "agentRetention",
            "Agent Retention cannot exceed Risk Rate",
            (value, context) => {
              if (!showRiskRate(context)) return true;
              const valueNumber = convertToNumber(value);
              const fee = convertToNumber(context.parent.actualRiskRate);
              return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
            }
          ),
      }),

      totalDue: yup.string().when("isValidationRequired", {
        is: true,
        then: yup
          .string()
          .test(
            "totalDue",
            "Total Due cannot exceed Premium/Fee",
            (value, context) => {
              if (showRiskRate(context)) return true;
              const valueNumber = convertToNumber(value);
              const fee = convertToNumber(context.parent.actualFee);
              return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
            }
          )
          .test(
            "totalDue",
            "Total Due cannot exceed Risk Rate",
            (value, context) => {
              if (!showRiskRate(context)) return true;
              const valueNumber = convertToNumber(value);
              const fee = convertToNumber(context.parent.actualRiskRate);
              return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
            }
          ),
      }),

      productItems: yup.array().of(
        yup.object({
          isValidationRequired: yup.boolean(),
          actualFee: yup.string(),
          actualRiskRate: yup.string().when("isValidationRequired", {
            is: true,
            then: yup
              .string()
              .test(
                "actualRiskRateItem",
                "Risk Rate cannot exceed Premium/Fee",
                (value, context) => {
                  if (!showRiskRate(context)) return true;
                  const valueNumber = convertToNumber(value);
                  const fee = convertToNumber(context.parent.actualFee);
                  return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
                }
              )
              .test(
                "actualRiskRateItem",
                "Risk Rate cannot be negative",
                (value, context) => {
                  if (!showRiskRate(context)) return true;
                  const valueNumber = convertToNumber(value);
                  return valueNumber >= 0;
                }
              ),
          }),

          agentRetention: yup.string().when("isValidationRequired", {
            is: true,
            then: yup
              .string()
              .test(
                "agentRetentionItem",
                "Agent Retention cannot exceed Premium/Fee",
                (value, context) => {
                  if (showRiskRate(context)) return true;
                  const valueNumber = convertToNumber(value);
                  const fee = convertToNumber(context.parent.actualFee);
                  return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
                }
              )
              .test(
                "agentRetentionItem",
                "Agent Retention cannot exceed Risk Rate",
                (value, context) => {
                  if (!showRiskRate(context)) return true;
                  const valueNumber = convertToNumber(value);
                  const fee = convertToNumber(context.parent.actualRiskRate);
                  return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
                }
              ),
          }),

          totalDue: yup.string().when("isValidationRequired", {
            is: true,
            then: yup
              .string()
              .test(
                "totalDueItem",
                "Total Due cannot exceed Premium/Fee",
                (value, context) => {
                  if (showRiskRate(context)) return true;
                  const valueNumber = convertToNumber(value);
                  const fee = convertToNumber(context.parent.actualFee);
                  return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
                }
              )
              .test(
                "totalDueItem",
                "Total Due cannot exceed Risk Rate",
                (value, context) => {
                  if (!showRiskRate(context)) return true;
                  const valueNumber = convertToNumber(value);
                  const fee = convertToNumber(context.parent.actualRiskRate);
                  return fee >= 0 ? valueNumber <= fee : valueNumber >= fee;
                }
              ),
          }),
        })
      ),
      originalPolicy: yup.object({
        isValidationRequired: yup.boolean(),
        date: yup.date().when("isValidationRequired", {
          is: true,
          then: yup
            .date()
            .nullable()
            .required(requiredErrorMessage)
            .max(
              getEndOfDay(new Date()),
              "Policy Date cannot be in the future"
            ),
        }),
        coverageType: yup
          .string()
          .nullable()
          .test("coverageType", requiredErrorMessage, (value, context) =>
            shouldValidateCoverageType(context) ? !!value : true
          ),
        liabilityAmount: yup.string().when("isValidationRequired", {
          is: true,
          then: yup
            .string()
            .test(
              "liabilityAmount must be a number",
              requiredErrorMessage,
              (value) => getRoundedValue(value) > 0
            ),
        }),
      }),
    })
  ),
});

const getIndividualFieldRequired = (value: string, field: yup.StringSchema) => {
  switch (value) {
    case "INDIVIDUAL":
    case "COUPLE":
      return field.required(requiredErrorMessage);
    default:
      return field;
  }
};

const getCompanyFieldRequired = (value: string, field: yup.StringSchema) => {
  switch (value) {
    case "INDIVIDUAL":
    case "COUPLE":
      return field;
    default:
      return field.required(requiredErrorMessage);
  }
};

const getAddressFieldRequired = (value: boolean, field: yup.StringSchema) => {
  if (value && value === true) {
    return field.required(requiredErrorMessage);
  } else {
    return field;
  }
};

const validateStringField = (value: string) => {
  return !!(value && value.trim().length > 0);
};

const getIndexFromPath = (path: string): number =>
  parseInt(path.split("[")[1].split("]")[0], 10);

const getValuesFromContext = (
  context: yup.TestContext<Record<string, any>>
): SCFile => {
  const contextFrom = (context as any).from;
  const values = contextFrom[contextFrom.length - 1].value;
  return values;
};
const getLiabilityConfig = (
  context: yup.TestContext<Record<string, any>>
): { form?: string; configValue?: string; }[] => {
  const contextPricinConfig = (context as any).options.context.liabilityConfig;
  return contextPricinConfig;
};

const getContextTsAgencyConfig = (
  context: yup.TestContext<Record<string, any>>
): SpaceAgencyProductStateConfiguration | undefined => {
  const contextTsAgencyConfig = (context as any).options.context.tsAgencyConfig;
  return contextTsAgencyConfig;
};

// The Collateral Mortgage trans code is configured per state, so it is resolved at
// validation time from the translation rows injected into the yup context by CreateFile.
const getCollateralMortgageTransCodeFromContext = (
  context: yup.TestContext<Record<string, any>>
): string => {
  const translations = (context as any).options.context
    ?.collateralMortgageTranslations;
  const values = getValuesFromContext(context);

  return getCollateralMortgageTransCode(
    translations,
    values?.properties?.[0]?.state?.abbr
  );
};

const validateAgencyLocation = (
  context: yup.TestContext<Record<string, any>>
): boolean => {
  const root = getValuesFromContext(context);
  const isEnabled = Boolean(
    root.fileNameNumber &&
    root.agency.id &&
    (root.agencyLocation.isValidationRequired ||
      root.agencyLocation.isDisabled)
  );
  return isEnabled;
};

// const validateProductAgencyLocation = (
//   value: string,
//   context: yup.TestContext<Record<string, any>>
// ): boolean => {
//   const root = getValuesFromContext(context);
//   const foundLocation = root.validLocations?.find(
//     (location) => location.locationDisplayName === value
//   );
//   return !!foundLocation;
// };

const validateProperties = (
  context: yup.TestContext<Record<string, any>>
): boolean => {
  const root = getValuesFromContext(context);
  const isEnabled = Boolean(
    root.fileNameNumber && root.agency.id && root.isPropertiesValidationRequired
  );
  return isEnabled;
};

function isIssuable(standaloneEndorsement: StandaloneEndorsement): boolean {
  return (
    !isNullOrUndefined(standaloneEndorsement.originalJacketNumber) &&
    !isNullOrUndefined(standaloneEndorsement.originalJacketDate) &&
    !isNullOrUndefined(standaloneEndorsement.originalJacketType) &&
    !isNullOrUndefined(standaloneEndorsement.originalJacketLiability) &&
    !isNullOrUndefined(standaloneEndorsement.originalJacketLiability) &&
    standaloneEndorsement.originalJacketLiability!.toString().length > 0
  );
}

const showRiskRate = (
  context: yup.TestContext<Record<string, any>>
): boolean => {
  const root = getValuesFromContext(context);
  return root.pricing.showRiskRate;
};
/* TODO: Remove this section after business confirms this should for sure be completely removed */
/*
const shouldValidateJacketOPN = (
  context: yup.TestContext<Record<string, any>>
): boolean => {
  const root = getValuesFromContext(context);
  const isValid =
    context.parent.isValidationRequired &&
    !root.pricing.isIntegratedPricing &&
    context.parent.pricingDetail.isReissue;
  return isValid;
};
*/

const shouldValidateCoverageType = (
  context: yup.TestContext<Record<string, any>>
): boolean => {
  const root = getValuesFromContext(context);
  return context.parent.isValidationRequired &&
    root.pricing.isIntegratedPricing;
};

const isValidDate = (date: Date | null) =>
  Boolean(date && !isNaN(date.getTime()));

const isValidZipCode = (code?: string) => {
  if (!code || code === "") return true;
  return VALID_ZIP_LENGTH.includes(code.length);
};

const hasZipCodeValidation = (
  context: yup.TestContext<Record<string, any>>
): boolean => {
  const root = getValuesFromContext(context);
  return Boolean(root.isZipCodeValidationRequired);
};

const isValidEndorsement = ({
  parent,
}: yup.TestContext<Record<string, any>>): boolean =>
  !parent.isValidationRequired ||
  (parent.isSFE && !parent.isSelected) ||
  (!parent.isSFE && !parent.endorsementName && !parent.effectiveDate);

const validateTitleSearchNeedByDate = (index: number, items: any): string => {
  if (!items || !items[index].needByDate) return "";
  if (!isValidDate(new Date(items[index].needByDate as string))) {
    return "Must be a valid date";
  }

  const needByDate = new Date(items[index].needByDate as string);
  const currentDate = (new Date()).setHours(0, 0, 0, 0);
  if (needByDate.getTime() < currentDate) {
    return "Must be today or a future date"
  }
  return "";
};

const validateEmails = (emailAddresses: string | undefined): string => {
  if (!emailAddresses) return "";
  const emails = emailAddresses?.replaceAll(",", ";")?.split(";");
  if (!emails || emails.length === 0) return "";

  let msg = "";
  for (let i = 0; i < emails.length; i++) {
    if (!validateEmail(emails[i].trim())) {
      msg = "Must be a valid email address";
      break;
    }

  }
  return msg;
};

function convertInDateFormat(str: any) {
  var date = new Date(str),
    mnth = ("0" + (date.getMonth() + 1)).slice(-2),
    day = ("0" + date.getDate()).slice(-2);
  return [mnth, day, date.getFullYear()].join("/");
}


const validateTitleSearchMessageNeedByDate = (index: number, items: any): string => {
  if (!items || !items[index].messageNeedByDate) return "";
  var date_regex = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;
  const formattedDate = convertInDateFormat(items[index].messageNeedByDate);

  if (!isValidDate(new Date(items[index].messageNeedByDate as string)) || (!date_regex.test(formattedDate))) {
    return "Must be a valid date";
  }
  const messageNeedByDate = new Date(items[index].messageNeedByDate as string);
  const currentDate = (new Date()).setHours(0, 0, 0, 0);

  if (messageNeedByDate.getTime() < currentDate) {
    return "Must be today or a future date"
  }
  return "";
};


export default validationSchema;
